import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import siteConfig from "../config/siteConfig";

const storageNamespace = siteConfig.brandName
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-");
const CART_STORAGE_KEY = `${storageNamespace}:cart`;
const WISHLIST_STORAGE_KEY = `${storageNamespace}:wishlist`;

export const CartContext = createContext({
  cartItems: [],
  wishlist: [],
});

const readStorage = (key, fallback) => {
  if (typeof window === "undefined") return fallback;

  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const lineMatches = (item, productId, size, color) =>
  item.id === productId && item.size === size && item.color === color;

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStorage(CART_STORAGE_KEY, [])
  );
  const [wishlist, setWishlist] = useState(() =>
    readStorage(WISHLIST_STORAGE_KEY, [])
  );

  useEffect(() => {
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    window.localStorage.setItem(
      WISHLIST_STORAGE_KEY,
      JSON.stringify(wishlist)
    );
  }, [wishlist]);

  const addToCart = useCallback(
    (productId, quantity = 1, size = "", color = "") => {
      const safeQuantity = Math.max(1, Number(quantity) || 1);

      setCartItems((items) => {
        const hasLine = items.some((item) =>
          lineMatches(item, productId, size, color)
        );

        if (!hasLine) {
          return [
            ...items,
            { id: productId, size, color, quantity: safeQuantity },
          ];
        }

        return items.map((item) =>
          lineMatches(item, productId, size, color)
            ? { ...item, quantity: item.quantity + safeQuantity }
            : item
        );
      });
    },
    []
  );

  const removeFromCart = useCallback((productId, size, color) => {
    setCartItems((items) =>
      items.filter((item) => !lineMatches(item, productId, size, color))
    );
  }, []);

  const updateQuantity = useCallback((productId, size, color, quantity) => {
    const nextQuantity = Math.max(1, Number(quantity) || 1);
    setCartItems((items) =>
      items.map((item) =>
        lineMatches(item, productId, size, color)
          ? { ...item, quantity: nextQuantity }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  const addToWishlist = useCallback((productId) => {
    setWishlist((items) =>
      items.includes(productId) ? items : [...items, productId]
    );
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    setWishlist((items) => items.filter((id) => id !== productId));
  }, []);

  const toggleWishlist = useCallback((productId) => {
    setWishlist((items) =>
      items.includes(productId)
        ? items.filter((id) => id !== productId)
        : [...items, productId]
    );
  }, []);

  const cartCount = useMemo(
    () => cartItems.reduce((count, item) => count + item.quantity, 0),
    [cartItems]
  );

  const value = useMemo(
    () => ({
      cartItems,
      cartCount,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      wishlist,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isInWishlist: (productId) => wishlist.includes(productId),
    }),
    [
      addToCart,
      addToWishlist,
      cartCount,
      cartItems,
      clearCart,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlist,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
